/* eslint-disable react/no-unescaped-entities */
import PillarPage from "./PillarPage";

const HERO = "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=1600";

const TOC = [
  { id: "how-it-works", label: "How off-plan works" },
  { id: "payment-plans", label: "Common payment-plan structures" },
  { id: "escrow", label: "How your money is protected" },
  { id: "risks", label: "Real risks & mitigations" },
  { id: "resale", label: "Reselling before handover" },
  { id: "best-devs", label: "Tier-1 developers in 2026" },
  { id: "worked-example", label: "Worked example — 40/60 plan" },
];

const SECTIONS = [
  {
    id: "how-it-works",
    heading: "How off-plan property in Dubai actually works",
    body: (
      <>
        <p>
          Off-plan means buying a unit directly from the developer <b>before construction is complete</b>.
          You sign a Sales & Purchase Agreement (SPA), pay a deposit (typically 5–20%), and settle the
          balance over the construction timeline. In exchange the developer gives you today's price for
          tomorrow's product — often 15–30% below the equivalent ready unit.
        </p>
        <p>
          The moment you sign, Dubai Land Department registers your interest under an <b>Oqood</b>
          certificate. Your payments flow into a RERA-regulated escrow account. When the tower hands
          over, your title deed is issued and the property is legally yours.
        </p>
      </>
    ),
  },
  {
    id: "payment-plans",
    heading: "The five payment-plan structures you'll see",
    body: (
      <table>
        <thead>
          <tr><th>Plan</th><th>Construction phase</th><th>Handover</th><th>Post-handover</th></tr>
        </thead>
        <tbody>
          <tr><td><b>60/40</b> — most common</td><td>60%</td><td>40%</td><td>—</td></tr>
          <tr><td><b>50/50</b> — buyer-friendly</td><td>50%</td><td>50%</td><td>—</td></tr>
          <tr><td><b>40/60</b> — cash-flow friendly</td><td>40%</td><td>60% (or 30% + 30% PHP)</td><td>Optional</td></tr>
          <tr><td><b>Post-handover 5-year</b></td><td>30–40%</td><td>10–20%</td><td>40–60% over 5 yrs</td></tr>
          <tr><td><b>1% per month</b> (DAMAC / select launches)</td><td>~24%</td><td>1%</td><td>75% over 75 months</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "escrow",
    heading: "How your money is protected — the escrow system",
    body: (
      <>
        <p>
          Every AED you pay for off-plan lands in a Central Bank supervised escrow account, <b>not the
          developer's operating account</b>. Funds are only released to the developer against construction
          milestones certified by an independent DLD-appointed engineer.
        </p>
        <ul>
          <li><b>Trust Account Law (Law 8 of 2007)</b> — mandates escrow for every off-plan development in Dubai.</li>
          <li><b>Milestone-based releases</b> — typical schedule: 20% at foundation, 40% at 40% construction, 60% at 60%, and so on.</li>
          <li><b>Escrow account number appears on your SPA</b> — always verify with the bank before your first payment.</li>
          <li><b>If the project is cancelled</b> by DLD, funds in escrow are refunded to buyers pro-rata.</li>
        </ul>
      </>
    ),
  },
  {
    id: "risks",
    heading: "The real risks — and how Point Penta mitigates them",
    body: (
      <table>
        <thead><tr><th>Risk</th><th>Mitigation</th></tr></thead>
        <tbody>
          <tr><td><b>Construction delay</b> (industry avg. 6–12 months over promised handover)</td><td>We only list projects from developers with a documented on-time record. Force-majeure clauses in SPAs cap developer liability — read them.</td></tr>
          <tr><td><b>Market softening before handover</b></td><td>Buy in-cycle communities Point Penta rates 7+/10 on our investment score. Yield-driven areas suffer less in downturns than pure-appreciation plays.</td></tr>
          <tr><td><b>Change of specification</b> (developer swaps materials)</td><td>SPA locks in the fit-out schedule. Compare finished units in the developer's previous projects before signing.</td></tr>
          <tr><td><b>Trapped equity if unable to resell pre-handover</b></td><td>Only buy in tower launches with strong resale liquidity (Downtown, Marina, Business Bay, Palm). Avoid isolated developments where you're the only exit.</td></tr>
          <tr><td><b>Developer default</b></td><td>Extremely rare for Tier-1 developers. Escrow refunds your paid amount if DLD cancels the project.</td></tr>
        </tbody>
      </table>
    ),
  },
  {
    id: "resale",
    heading: "Selling your off-plan before handover",
    body: (
      <ol>
        <li><b>Reach the developer's assignment threshold</b> — typically 30–40% of the price must be paid before you can resell.</li>
        <li><b>Apply for an NOC</b> (No Objection Certificate) from the developer. Typical fee: AED 1,500 – 5,000.</li>
        <li><b>Buyer signs a new SPA</b> and takes over remaining payments. DLD processes the reassignment.</li>
        <li><b>DLD fee applies again</b> to the reassignment (4% on new sale price). The buyer typically pays.</li>
        <li><b>Point Penta handles the entire reassignment</b> end-to-end — most complete within 3 weeks.</li>
      </ol>
    ),
  },
  {
    id: "best-devs",
    heading: "Tier-1 Dubai developers in 2026",
    body: (
      <ul>
        <li><b>Emaar Properties</b> — Downtown Dubai, Dubai Hills, Emaar Beachfront. On-time delivery, unmatched brand.</li>
        <li><b>Nakheel</b> — Palm Jumeirah, Deira Islands, Dubai Islands. Waterfront specialist.</li>
        <li><b>DAMAC Properties</b> — DAMAC Hills, DAMAC Lagoons. Luxury villas + branded residences (Cavalli, Versace).</li>
        <li><b>Sobha Realty</b> — Sobha Hartland, Sobha One. Superior fit-out and quality control.</li>
        <li><b>Meraas</b> — City Walk, Bluewaters, La Mer. Lifestyle-led design and mixed-use.</li>
        <li><b>Aldar Properties</b> — Abu Dhabi's largest developer, expanding into Dubai (Aldar Haven, Athlon).</li>
        <li><b>Select Group</b> — Dubai Marina specialist (Marina Gate, No.9, Six Senses The Palm).</li>
        <li><b>Ellington Properties</b> — Boutique launches with premium design ratios.</li>
        <li><b>Dubai Properties</b> — Business Bay master developer.</li>
      </ul>
    ),
  },
  {
    id: "worked-example",
    heading: "Worked example — AED 2M unit on a 40/60 plan",
    body: (
      <table>
        <thead><tr><th>Milestone</th><th>%</th><th>Amount (AED)</th><th>Timeline</th></tr></thead>
        <tbody>
          <tr><td>SPA signature</td><td>10%</td><td>200,000</td><td>Day 0</td></tr>
          <tr><td>Foundation</td><td>10%</td><td>200,000</td><td>Month 6</td></tr>
          <tr><td>20% construction</td><td>5%</td><td>100,000</td><td>Month 12</td></tr>
          <tr><td>40% construction</td><td>5%</td><td>100,000</td><td>Month 18</td></tr>
          <tr><td>60% construction</td><td>5%</td><td>100,000</td><td>Month 24</td></tr>
          <tr><td>80% construction</td><td>5%</td><td>100,000</td><td>Month 30</td></tr>
          <tr><td><b>Handover</b></td><td>60%</td><td>1,200,000</td><td>Month 36</td></tr>
        </tbody>
      </table>
    ),
  },
];

const FAQS = [
  { q: "What is off-plan property in Dubai?", a: "Off-plan means buying a unit directly from the developer before construction is complete. Payments are staggered across construction milestones (typically 40–70% during construction, 30–60% at handover). It offers pricing 15–30% below equivalent ready units in exchange for a 24–48 month wait." },
  { q: "Is off-plan property in Dubai safe to buy?", a: "Yes, when purchased through RERA-registered escrow. Every AED you pay lands in a Central Bank supervised escrow account that only releases to the developer against verified construction milestones. Point Penta only lists projects with active escrow and Tier-1 developer track records." },
  { q: "Which Dubai developers are the safest for off-plan in 2026?", a: "Emaar, Nakheel, DAMAC, Sobha, Meraas, Aldar, Select Group, Ellington and Dubai Properties are the Tier-1 masters with documented on-time delivery. Point Penta partners directly with each and can secure launch-day pricing." },
  { q: "Can I resell my off-plan unit before handover?", a: "Yes, once you've paid the developer's assignment threshold (typically 30–40%). You'll need an NOC from the developer (AED 1,500 – 5,000) and the buyer takes over remaining payments via a reassignment at DLD. Point Penta manages the entire process." },
  { q: "What happens if a Dubai off-plan project is cancelled?", a: "If Dubai Land Department cancels the project (extremely rare for Tier-1 developers), funds held in escrow are refunded to buyers pro-rata. This is why buying only from RERA-approved developments with active escrow is non-negotiable." },
  { q: "What is a good off-plan payment plan?", a: "For cash-flow-friendly investors, 40/60 (40% during construction, 60% at handover) or post-handover 5-year plans give the most flexibility. Aggressive buyers seeking maximum appreciation often choose 60/40 or 50/50 to lock in early." },
  { q: "Do I pay the 4% DLD fee on off-plan property?", a: "Yes, the 4% DLD fee applies to off-plan purchases at Oqood registration or handover (varies by developer). Many developers absorb this fee entirely during launch promotions — always confirm in the SPA." },
];

export default function OffPlanPaymentPlansPillar() {
  return (
    <PillarPage
      slug="dubai-off-plan-payment-plans"
      category="Off-plan investment"
      title="Dubai Off-Plan Payment Plans Explained (2026)"
      hero={HERO}
      updated="Feb 2026"
      intro="Off-plan Dubai property lets you lock in today's price for tomorrow's unit — with the balance spread over 24–48 months of construction. This guide breaks down the five payment-plan structures you'll actually see, how escrow protects your money, the real risks, resale mechanics, and Tier-1 developer picks for 2026."
      toc={TOC}
      sections={SECTIONS}
      faqs={FAQS}
    />
  );
}
